import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';

/** Push-only Socket.IO client for live broadcast delivery (`broadcast.new`). */
@Injectable({
  providedIn: 'root'
})
export class SocketService {
  private socket: Socket | null = null;
  private notificationSubject = new BehaviorSubject(null);
  private broadcastSubject = new BehaviorSubject(null);
  private readySubject = new BehaviorSubject(false);
  private connectPromise: Promise<void> | null = null;

  readonly connected$ = this.readySubject.asObservable();

  constructor() {
    window.addEventListener('beforeunload', () => {
      this.disconnect();
    });
  }

  private getAuthPayload() {
    const currentUser = localStorage.getItem('current_user') ? JSON.parse(localStorage.getItem('current_user')) : null;
    if (!currentUser?.email) return null;
    return {
      email: currentUser.email,
      token: currentUser.accessToken || undefined
    };
  }

  private getSocketOptions() {
    const auth = this.getAuthPayload();
    const useProxy = environment.commUrl?.startsWith('/');
    const transports: ('websocket' | 'polling')[] =
      environment.production ? ['websocket'] : ['websocket', 'polling'];

    return {
      auth,
      withCredentials: true,
      transports,
      url: useProxy ? window.location.origin : (environment.commFullUrl || window.location.origin),
      path: useProxy ? `${environment.commUrl.replace(/\/$/, '')}/socket.io` : '/socket.io'
    };
  }

  connectIfNeeded = (): void => {
    const opts = this.getSocketOptions();
    if (!opts.auth) {
      return;
    }

    if (this.socket?.connected) {
      return;
    }

    if (this.connectPromise) {
      return;
    }

    if (this.socket && !this.socket.connected) {
      this.socket.auth = opts.auth;
      this.socket.connect();
      return;
    }

    this.connectPromise = new Promise<void>((resolve) => {
      this.socket = io(opts.url, {
        path: opts.path,
        withCredentials: opts.withCredentials,
        transports: opts.transports,
        auth: opts.auth,
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 500,
        reconnectionDelayMax: 2000,
        timeout: 10000,
        autoConnect: true
      });

      this.bindListeners(this.socket);

      const onConnect = () => {
        this.socket?.off('connect', onConnect);
        resolve();
      };

      if (this.socket.connected) {
        resolve();
      } else {
        this.socket.on('connect', onConnect);
      }
    }).finally(() => {
      this.connectPromise = null;
    });
  };

  reconnect = () => {
    this.disconnect();
    this.connectIfNeeded();
  };

  getNotification() {
    return this.notificationSubject.asObservable();
  }

  getBroadcast() {
    return this.broadcastSubject.asObservable();
  }

  private markReady(): void {
    this.readySubject.next(true);
  }

  private markNotReady(): void {
    this.readySubject.next(false);
  }

  private bindListeners(socket: Socket) {
    socket.on('connect', () => {
      console.log('Comm socket connected');
    });

    socket.on('connect_error', (err) => {
      console.error('Comm socket connection error:', err?.message || err);
      this.markNotReady();
    });

    socket.on('disconnect', (reason) => {
      console.warn('Comm socket disconnected:', reason);
      this.markNotReady();
    });

    socket.on('connection.ready', (data) => {
      this.markReady();
      console.log('Comm socket ready', data?.data || data);
    });

    socket.on('notification.new', (msg) => this.notificationSubject.next(msg?.payload || msg));
    socket.on('broadcast.new', (msg) => {
      const payload = msg?.payload || msg;
      this.broadcastSubject.next(payload);
    });
  }

  disconnect = () => {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.connectPromise = null;
    this.markNotReady();
  };
}
